import React, { useContext, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import T from '@prisjakt/ui-translate';
import Pagination from '@prisjakt/ui-pagination';
import { ScrollLink, ScrollElement } from '@prisjakt/ui-scroll';
import { bemGenerator } from '@prisjakt/ui-base';
import ProductImage from '@prisjakt/ui-product-image';
import CampaignBanner from '../../../campaign/components/banner';
import Lazy from '../../../../components/lazy';
import AdContainer from '../../../../components/ads';
import ProductListingHeader from '../header';
import CampaignBadge from '../../../../components/campaign-badge';
import Product from './components/product';
import AdvizedWidget from '../../../../components/advized';
import { useViewportContext } from '../../../../components/viewport/context';
import { useCampaignsByProductId } from '../../../../hooks/campaigns-by-product-id';

import { CategoryProductListingFragment } from '../../../../types';
import './index.scss';

const styles = bemGenerator('pj-ui-product-listing', [
  'item',
  'columns',
  'column-cell',
  'table-body',
  'tooltip-trigger',
  'information',
]);

const SCROLL_KEY = 'Product Listing';

const addStickyOffset = () => {
  if (window.innerWidth < 640) {
    return 50;
  }

  return 70;
};

const getSelectedProductProperties = (properties, selectedProperties) => {
  if (
    !properties ||
    properties.length === 0 ||
    !selectedProperties ||
    selectedProperties.length === 0
  ) {
    return [];
  }

  const selectedIds = selectedProperties.map(x => x.id);

  // Sort properties into the correct order to match headers
  return properties
    .filter(x => selectedIds.includes(x.id))
    .sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id));
};

const getBadge = (campaigns, activeCampaign, viewMode) => {
  const campaign =
    campaigns &&
    campaigns.find(c => String(c.id) === String(activeCampaign.id));

  if (!campaign) {
    return null;
  }

  return viewMode === 'table' ? (
    <CampaignBadge campaign={campaign} />
  ) : (
    <CampaignBanner type="banner_logo_big" banners={activeCampaign.banners} />
  );
};

const getPriceBySortMode = (prod, sortMode) => {
  const defaultPrice =
    prod.priceSummary.regular || prod.priceSummary.alternative;

  if (!sortMode.name) {
    return defaultPrice;
  }

  switch (sortMode.name) {
    case 'price':
      return defaultPrice;
    case 'price_include_shipping':
      return prod.priceSummary.includeShipping || defaultPrice;
    default:
      return defaultPrice;
  }
};

const ProductListing = ({
  children,
  header,
  products,
  pageInfo,
  mode,
  withFilter,
  activeCampaign,
  selectedProperties,
  onSortChange,
  loading,
  fallbackImage,
  showPagination,
  showRatings,
  showCategory,
  showProductSelector,
  onProductSelect,
  onProductDeselect,
  onProductClicked,
  selectedProducts,
  adverts,
  advized,
}) => {
  const { t, market } = useContext(AppContext);
  const { nodes } = products;

  const { isMobile, isTablet } = useViewportContext();
  const productIds = useMemo(() => nodes.map(product => product.id), [nodes]);
  const campaigns = useCampaignsByProductId(productIds);

  const productListItems = nodes.map((prod, index) => {
    if (campaigns && prod.id.toString() in campaigns) {
      // eslint-disable-next-line no-param-reassign
      prod.campaigns = campaigns[prod.id.toString()].map(c => ({ id: c }));
    }

    return (
      <Product
        key={prod.id}
        id={prod.id}
        mode={mode}
        link={prod.pathName}
        name={prod.name}
        image={
          <Lazy offset={0}>
            <ProductImage
              name={prod.name}
              src={prod.media.first}
              fallback={fallbackImage}
            />
          </Lazy>
        }
        price={getPriceBySortMode(prod, pageInfo.sort)}
        rating={showRatings ? prod.userReviewSummary.rating : null}
        ratingCount={prod.userReviewSummary.count}
        category={showCategory ? prod.category : null}
        campaignBadge={
          activeCampaign && getBadge(prod.campaigns, activeCampaign, mode)
        }
        brand={prod.brand}
        storesCount={prod.priceSummary.count}
        storesCountText={t('campaigns', 'item.sold-by-n-stores', {
          n: prod.priceSummary.count,
        })}
        properties={getSelectedProductProperties(
          prod.properties && prod.properties.nodes,
          selectedProperties
        )}
        selectable={showProductSelector}
        selected={
          showProductSelector &&
          Boolean(selectedProducts.find(product => product.id === prod.id))
        }
        onSelect={onProductSelect}
        onDeselect={onProductDeselect}
        onClick={() => {
          const offset = pageInfo.offset ? pageInfo.offset : 0;
          const clickIndex = index + 1 + offset;
          onProductClicked(clickIndex);
        }}
      />
    );
  });

  const advertSpacing = { top: '10px', bottom: '10px' };
  const ads = {
    desktopInline: (
      <AdContainer
        scalable
        scaleBase=".pj-ui-panel"
        scaleOrigin="left center"
        placement="panorama_extra"
        format="panorama_1_wde"
        min={861}
        max={10000}
        spacing={advertSpacing}
      />
    ),
    mobileInline1: (
      <AdContainer
        placement="dubbelpanorama1"
        format="panorama_1"
        min={0}
        max={860}
        spacing={advertSpacing}
      />
    ),
    mobileInline2: (
      <AdContainer
        placement="dubbelpanorama2"
        format="panorama_2"
        min={0}
        max={860}
        spacing={advertSpacing}
        triggerPoint={100}
      />
    ),
    mobileInline3: (
      <AdContainer
        placement="dubbelpanorama2"
        format="panorama_3"
        min={0}
        max={860}
        spacing={advertSpacing}
        triggerPoint={100}
      />
    ),
    ...adverts,
  };

  const adWrapper = (ad, adClass) => (
    <li
      className={`${styles.item.withModifier({
        [mode]: true,
      })} ${adClass}`}
    >
      {ad}
    </li>
  );

  const hasAdvizedWidget =
    mode === 'grid' && pageInfo.offset === 0 && advized && advized.selected;
  // eslint-disable-next-line no-nested-ternary
  const advizedWidgetOrder = isMobile ? 1 : isTablet ? 2 : 3;
  return (
    <ScrollElement name={SCROLL_KEY}>
      <Panel
        layoutMode={mode}
        header={header}
        lines
        footer={
          <>
            {showPagination && (
              <ScrollLink
                to={SCROLL_KEY}
                shouldScroll={({ tagName }) => tagName === 'A'}
                calculateOffset={addStickyOffset}
              >
                <Pagination pageInfo={pageInfo} />
              </ScrollLink>
            )}
            {market.showProductInformationTexts && nodes.length > 0 && (
              <div className={styles.information}>
                <T domain="category">product-list.data-information</T>
              </div>
            )}
          </>
        }
        data-test-type="product-listing"
      >
        {mode !== 'table' && (
          <ul
            className={styles.block.withModifier({
              'with-filter': withFilter,
              [mode]: true,
              loading,
            })}
          >
            {adWrapper(ads.desktopInline, 'desktop-inline-ad')}
            {adWrapper(ads.mobileInline1, 'mobile-inline-ad-1')}

            {nodes.length > 9 &&
              adWrapper(ads.mobileInline2, 'mobile-inline-ad-2')}
            {mode === 'grid' &&
              nodes.length > 18 &&
              adWrapper(ads.mobileInline3, 'mobile-inline-ad-3')}

            {hasAdvizedWidget && <AdvizedWidget {...advized} />}
            {productListItems.map((prod, index) => (
              <li
                className={styles.item.withModifier({ [mode]: true })}
                key={prod.key}
                style={{
                  order:
                    hasAdvizedWidget && index >= advizedWidgetOrder
                      ? index + 1
                      : index,
                }}
              >
                {prod}
              </li>
            ))}
          </ul>
        )}

        {mode === 'table' && (
          <div
            className={styles.block.withModifier({
              'with-filter': withFilter,
              [mode]: true,
              loading,
            })}
          >
            <>
              <ProductListingHeader
                firstColumnIndent={showProductSelector ? 2 : 1}
                selectedProperties={selectedProperties}
                onSortChange={onSortChange}
                sortInfo={pageInfo.sort}
              />
              <ul
                className={styles['table-body'].withModifier({ loading })}
                data-test="ProductTable"
              >
                {productListItems.slice(0, 2)}
                {adWrapper(ads.mobileInline1, 'mobile-inline-ad-1')}
                {adWrapper(ads.desktopInline, 'desktop-inline-ad')}
                {productListItems.slice(2, 10)}
                {productListItems.length > 9 &&
                  adWrapper(ads.mobileInline2, 'mobile-inline-ad-2')}
                {productListItems.slice(10)}
              </ul>
            </>
          </div>
        )}

        {children}
      </Panel>
    </ScrollElement>
  );
};

const { number, shape, string, bool, node, arrayOf } = PropTypes;

ProductListing.propTypes = {
  children: node,
  withFilter: bool.isRequired,
  pageInfo: shape({
    offset: number,
    limit: number,
    hasNext: bool,
    hasPrevious: bool,
  }),
  header: node.isRequired,
  mode: string.isRequired,
  products: graphqlPropTypes(CategoryProductListingFragment),
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        url: string,
        type: string,
        imageUrl: string,
      })
    ),
  }).isRequired,
  propertySummaryMode: string.isRequired,
  selectedProperties: arrayOf(shape({ id: string })).isRequired,
  onSortChange: PropTypes.func,
  loading: bool,
  fallbackImage: string,
  showPagination: bool,
  showRatings: bool,
  showCategory: bool,
  showProductSelector: bool,
  onProductSelect: PropTypes.func,
  onProductDeselect: PropTypes.func,
  onProductClicked: PropTypes.func,
  selectedProducts: PropTypes.array,
  adverts: PropTypes.shape({
    desktopInline: PropTypes.element,
    mobileInline1: PropTypes.element,
    mobileInline2: PropTypes.element,
  }),
};

ProductListing.defaultProps = {
  children: null,
  products: undefined,
  pageInfo: undefined,
  loading: false,
  onSortChange: () => {},
  fallbackImage: undefined,
  showPagination: true,
  showRatings: true,
  showCategory: false,
  showProductSelector: false,
  onProductSelect: () => {},
  onProductDeselect: () => {},
  onProductClicked: () => {},
  selectedProducts: [],
  adverts: {},
};

export default connect(state => ({
  activeCampaign: state.globals.campaigns.active.find(c => c.type === 'OFFER'),
  loading: state.areas.category.productList.loading,
}))(memo(ProductListing));
